import { computed, defineComponent, ref, unref, watch, type MaybeRef } from 'vue';
import type { IMultiSelectProps } from './types';
import { EMultiSelectEvent, useMultiSelect, type UseMultiSelect } from './hook';
import { defineField, type FormFieldOption } from '@/form/useForm';
import { usePopover, type UsePopover } from '../popover/hook';
import { isNullish } from '@/utils/is';
import CheckboxInput from '../form/checkbox-input';
import { useFormField } from '@/form/useForm/useFormField';
import { tv } from 'tailwind-variants';
import { getRefId } from 'findef';
import BasicInput from '../form/basic-input';
import { Spinner } from '../spinner';
import { twColor } from '@/utils/style';
import { onClickOutside } from '@vueuse/core';
import Icon from 'components/icon';
import { lerp, smoothstep } from 'math';
import Button from '../button';
import ProgressBar from '../progress-bar';

const ITEM_HEIGHT = 42;

const itemClass = tv({
  slots: {
    base: 'px-2 md:px-4 text-sm cursor-pointer select-none',
  },
  variants: {
    selected: {
      true: {
        base: 'bg-primary-100',
      },
    },
    placeholder: {
      true: {
        base: 'hover:bg-gray-100',
      },
      false: {
        base: 'hover:bg-primary-100',
      },
    },
  },
});

const Item = defineComponent<{
  item: MaybeRef<FormFieldOption>;
  hook: UseMultiSelect;
  placeholder?: boolean;
  popover?: UsePopover;
  itemHeight?: number;
}>(
  (props) => {
    const item = computed((): FormFieldOption => unref(props.item));
    const isSelected = computed(() => !!item.value.selected);

    const field = useFormField<boolean>({
      schema: defineField({
        type: 'boolean',
        value: !!isSelected.value,
      }),
      onChange: (value) => {
        if (value === true) {
          props.hook.select(item.value.value);
        } else {
          props.hook.unselect(item.value.value);
        }
      },
    });

    const handleClick = () => {
      if (props.hook.disabled.value) return;
      if (!props.placeholder) {
        props.hook.toggleSelect(item.value.value);
      }
    };

    const clazz = itemClass({});

    const animationValue = computed(() =>
      props.popover ? props.popover.animation.current.value : 0,
    );
    const rotation = computed(() => {
      const f = animationValue.value;
      const sf = smoothstep(0.0, 1.0, f);
      return lerp(0.0, 180.0, sf);
    });

    const innerContent = computed(() => {
      const BodyComponent = item.value.body;
      const label = item.value.label || '';
      const BodyLeft = item.value.bodyLeft;
      const iconLeft = item.value.iconLeft;

      if (BodyComponent) {
        return <BodyComponent />;
      } else if (BodyLeft) {
        return (
          <div class="grid grid-cols-[max-content_1fr] w-full h-full items-center gap-2">
            <BodyLeft />
            <div class="flex items-center h-full w-full">{label}</div>
          </div>
        );
      }

      if (iconLeft) {
        return (
          <div class="grid grid-cols-[max-content_1fr] w-full h-full items-center gap-3">
            <div>
              <Icon src={iconLeft} />
            </div>
            <div class="flex items-center h-full w-full">{label}</div>
          </div>
        );
      }

      return <div class="flex items-center h-full w-full">{label}</div>;
    });

    return () => {
      return (
        <div
          onClick={handleClick}
          class={clazz.base({ selected: !!isSelected.value, placeholder: !!props.placeholder })}
          style={{
            height: props.itemHeight + 'px',
          }}
        >
          {props.placeholder ? (
            <div class="grid grid-cols-[max-content_max-content] items-center justify-between w-full h-full">
              {innerContent.value}
              <div class="text-gray-500">
                <Icon
                  src={async () => import('assets/img/svg/icons/chevron_down.svg?raw')}
                  size="1rem"
                  fill="currentColor"
                  stroke="none"
                  style={{
                    rotate: `${rotation.value}deg`,
                  }}
                />
              </div>
            </div>
          ) : (
            <div class="grid grid-cols-[max-content_1fr] items-center gap-4 w-full h-full">
              <div class="pointer-events-none">
                <CheckboxInput variant="primary" value={isSelected.value} dataRef={field.dataRef} />
              </div>
              {innerContent.value}
            </div>
          )}
        </div>
      );
    };
  },
  { props: ['item', 'hook', 'placeholder', 'popover', 'itemHeight'] },
);

const ItemList = defineComponent<{
  hook: UseMultiSelect;
  popover: UsePopover;
  manuallyAddAlwaysVisible?: boolean;
  manuallyAddText?: string;
  itemHeight?: number;
}>(
  (props) => {
    const field = useFormField<string>({
      schema: defineField({
        type: 'text',
        value: '',
        placeholder: 'Search',
      }),
      onChange: (value) => {
        props.hook.setQueryString(value);
      },
    });

    const handleManuallyAdd = async () => {
      if (!props.hook.addManually) return;
      props.popover.setOpen(false);
      const item = await props.hook.addManually(props.hook.queryString.value || '');
      props.hook.select(item);
    };

    const manuallyAddIsVisible = computed(
      () =>
        ((props.hook.queryString.value && props.hook.queryString.value.length) ||
          !!props.manuallyAddAlwaysVisible) &&
        !!props.hook.addManually,
    );

    const manuallyAddText = computed(() => {
      if (props.hook.queryString.value && props.hook.queryString.value.length > 0) {
        return `Manually add "${props.hook.queryString.value}"`;
      }
      return props.manuallyAddText || 'Create New';
    });

    const height = computed(
      () => props.hook.maxVisibleItems.value * (props.itemHeight ?? ITEM_HEIGHT),
    );
    return () => {
      return (
        <div
          class={[
            'grid bg-white shadow-sm rounded-sm overflow-hidden',
            manuallyAddIsVisible.value
              ? 'grid-rows-[max-content_1fr_max-content]'
              : 'grid-rows-[max-content_1fr]',
          ]}
        >
          <div class="p-4">
            <BasicInput
              type="text"
              placeholder={field.dataRef.attributes.value.placeholder}
              dataRef={field.dataRef}
              value={field.dataRef.value.value || ''}
              iconLeft={{
                src: async () => import('assets/img/svg/icons/search.svg?raw'),
                size: '1rem',
                stroke: twColor('gray-400'),
                fill: 'none',
              }}
            />
          </div>
          {props.hook.isFetching.value ? (
            <div
              class="grid items-center justify-center"
              style={{
                height: height.value + 'px',
              }}
            >
              <Spinner />
            </div>
          ) : (
            <div
              style={{
                maxHeight: height.value + 'px',
                overflow: 'auto',
              }}
              class="bg-white pretty-scrollbar"
            >
              {props.hook.items.value.map((item, i) => {
                return (
                  <Item
                    item={item}
                    hook={props.hook}
                    key={item.key ?? `item-${i}-${getRefId(item.value)}`}
                    itemHeight={props.itemHeight ?? ITEM_HEIGHT}
                  />
                );
              })}
            </div>
          )}

          {manuallyAddIsVisible.value && (
            <div
              onClick={handleManuallyAdd}
              class="group w-full h-16 px-4 flex items-center cursor-pointer"
            >
              <div class="h-full flex items-center w-full border-t border-gray-100">
                <div class="grid grid-cols-[max-content_max-content] items-center gap-2 transition-opacity group-hover:opacity-80">
                  <Button
                    style={{ height: '1.25rem', width: '1.25rem', aspectRatio: '1 / 1' }}
                    variant="secondary"
                    size="s"
                    onlyIcon
                    icon={{
                      src: async () => import('assets/img/svg/icons/plus.svg?raw'),
                      fill: twColor('primary-500'),
                      size: '0.75rem',
                    }}
                  />
                  <span class="text-primary-500 text-sm font-semibold font-['Inter']">
                    {manuallyAddText.value}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };
  },
  { props: ['hook', 'popover', 'manuallyAddAlwaysVisible', 'manuallyAddText', 'itemHeight'] },
);

export const MultiSelect = defineComponent<IMultiSelectProps>(
  (props) => {
    let updateLock: boolean = false;

    const getValues = (x: any) => {
      const val = unref(x);
      if (Array.isArray(val)) return val;
      return isNullish(val) || val === '' ? [] : [val];
    };
    const initialValues = ref<any[]>(getValues(unref(props.value)));

    watch(
      () => unref(props.value),
      (val) => {
        updateLock = true;
        initialValues.value = getValues(val);

        // prevents circular updates
        setTimeout(() => {
          updateLock = false;
        }, 60);
      },
    );

    const isDisabled = computed(() => !!unref(props.disabled));

    const hook =
      props.hook ||
      useMultiSelect({
        options: props.options || { items: [] },
        values: initialValues,
        disabled: isDisabled,
      });

    hook.events.subscribe(EMultiSelectEvent.SELECTION_CHANGE, (ev) => {
      if (!updateLock && props.dataRef) {
        props.dataRef.setValue(ev.selection);
      }
    });

    const itemListRef = ref();
    const targetRef = ref();
    const popover = usePopover({
      target: targetRef,
      self: itemListRef,
      fitTargetWidth: true,
    });

    const handleClickTarget = () => {
      popover.toggleOpen();
    };

    const selectionCount = computed((): number => hook.selected.value.length);

    const itemSingularName = computed(() => props.itemSingularName || 'item');
    const itemPluralName = computed(() => props.itemPluralName || 'items');

    const displayedItem = computed((): FormFieldOption => {
      return {
        value: null,
        label:
          selectionCount.value > 0
            ? selectionCount.value === 1
              ? `${selectionCount.value} ${itemSingularName.value} selected`
              : `${selectionCount.value} ${itemPluralName.value} selected`
            : props.placeholder || 'Select',
        iconLeft: props.icon || {
          src: async () => import('assets/img/svg/icons/list.svg?raw'),
          size: '1rem',
          color: twColor('gray-400'),
        },
      };
    });

    onClickOutside(
      itemListRef,
      () => {
        if (popover.isOpen.value) {
          popover.setOpen(false);
        }
      },
      { ignore: [targetRef] },
    );

    const resolvedItemHeight = computed(() => props.itemHeight ?? ITEM_HEIGHT);

    return () => {
      return (
        <div class="min-w-[100px] select-none">
          <div
            onClick={handleClickTarget}
            ref={targetRef}
            class="w-full border border-gray-300"
            style={{
              height: ITEM_HEIGHT + 'px',
              overflow: 'hidden',
            }}
          >
            <Item
              popover={popover}
              hook={hook}
              item={displayedItem}
              placeholder={true}
              itemHeight={ITEM_HEIGHT}
            />
          </div>
          <ItemList
            ref={itemListRef}
            hook={hook}
            popover={popover}
            style={popover.style.value}
            manuallyAddAlwaysVisible={props.manuallyAddAlwaysVisible}
            manuallyAddText={props.manuallyAddText}
            itemHeight={resolvedItemHeight.value}
          />
          {popover.isOpen.value === false && hook.isAdding.value && (
            <ProgressBar size="xs" scale100 square value={hook.elapsedProgress.value} />
          )}
        </div>
      );
    };
  },
  {
    props: [
      'hook',
      'value',
      'options',
      'dataRef',
      'placeholder',
      'type',
      'icon',
      'itemSingularName',
      'itemPluralName',
      'manuallyAddAlwaysVisible',
      'manuallyAddText',
      'disabled',
      'itemHeight',
    ],
  },
);
